import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { AuditLogDetailModal } from '../../features/administration/audit-logs/components/AuditLogDetailModal';
import { auditLogsApi } from '../../features/administration/audit-logs/auditLogsApi';
import type { AuditLogListItem } from '../../features/administration/audit-logs/auditLogTypes';
import { formatDateTime, toIso } from '../../lib/formatDate';
import { ApiError } from '../../lib/apiError';
import { EmptyState } from '../EmptyState';
import { ErrorState } from '../ErrorState';
import { Skeleton } from '../Skeleton';
import { IndexPagination } from './IndexPagination';

const PAGE_SIZE = 30;

/**
 * An audit trail — frontend reference §13 — as a list that sits wherever it is put.
 *
 * **ONE list, two hosts.** `<HistoryLogsModal>` renders it for a module's history from the index
 * cog; a profile renders it in place as the record's own trail. The same depth either way: every
 * row opens the shared `<AuditLogDetailModal>` with its field-by-field changes, scoped through
 * the one `/api/audit-logs` endpoint, paginated, never a fixed "Latest N" window.
 *
 * **The host decides WHEN it exists, and mounting is the fetch.** The modal mounts it while open
 * and a profile while its tab is open, so a closed trail has fetched nothing and its search and
 * page reset by unmounting.
 */
export function AuditTrailList({
  module,
  entityTypes,
  entityId,
  emptyMessage,
}: {
  /** The id prefix — the module key. */
  module: string;
  /**
   * MODULE history: every type the module's workflow touches — parent AND children. Pass the
   * module's one shared list, never a literal. Leave it off for a RECORD's history.
   */
  entityTypes?: string[];
  /**
   * RECORD history: every row about this record **or about one of its sections**, which the
   * server resolves through the audit row's parent. **By id OR by type, never both** — a refused
   * write never reached an entity, carries no type, and is exactly what a record's trail is for.
   */
  entityId?: string;
  emptyMessage: string;
}) {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [rows, setRows] = useState<AuditLogListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [detailId, setDetailId] = useState<number | null>(null);

  // Debounced to 300ms, matching every other search in the product.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    auditLogsApi
      .list(
        {
          /*
            Absent, never empty. An axis sent with nothing on it is "ticked to nothing" and
            matches NO row — see the three-state facet rule — so a caller scoping by record
            alone would get an empty history rather than an unconstrained one.
          */
          entityType: entityTypes?.length ? entityTypes.join(',') : undefined,
          entityId,
          search,
          page,
          pageSize: PAGE_SIZE,
        },
        controller.signal,
      )
      .then((result) => {
        setRows(result.items);
        setTotal(result.total);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(caught instanceof ApiError ? caught : new ApiError('Could not load history.', 0));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [entityTypes, entityId, search, page]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const firstRow = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const lastRow = Math.min(page * PAGE_SIZE, total);

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <div className="relative shrink-0">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            aria-hidden="true"
          />
          <input
            id={`${module}-history-search`}
            name={`${module}-history-search`}
            type="search"
            autoComplete="off"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search actor, record, or path"
            aria-label="Search history"
            className="min-h-10 w-full rounded-lg border border-slate-300 bg-white py-2 pl-10 pr-3 text-sm text-slate-800 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </div>

        <p className="shrink-0 text-xs text-slate-500">
          {loading ? (
            <Skeleton className="h-3.5 w-40" />
          ) : (
            <>
              <span className="font-mono tabular-nums">{total}</span> audit{' '}
              {total === 1 ? 'entry' : 'entries'} · page{' '}
              <span className="font-mono tabular-nums">{page}</span> of{' '}
              <span className="font-mono tabular-nums">{pageCount}</span>
            </>
          )}
        </p>

        <div className="modern-scrollbar min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          {error ? (
            <ErrorState message={error.message} correlationId={error.correlationId} />
          ) : loading ? (
            <ul className="flex flex-col gap-2">
              {Array.from({ length: 6 }, (_, i) => (
                <li key={i} className="rounded-lg border border-slate-200 p-3">
                  <Skeleton className="h-4 w-2/3" />
                </li>
              ))}
            </ul>
          ) : rows.length === 0 ? (
            <EmptyState
              message={search ? `No history matches "${search}".` : emptyMessage}
              minHeight="min-h-[160px]"
            />
          ) : (
            <ol className="flex flex-col gap-2">
              {rows.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    id={`${module}-history-row-${row.id}`}
                    onClick={() => setDetailId(row.id)}
                    className="w-full rounded-lg border border-slate-200 bg-white p-3 text-left transition hover:border-slate-300 hover:bg-slate-50"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="text-sm text-slate-700">
                        {row.action}
                        {row.entityDisplay ? <span className="text-slate-500"> — {row.entityDisplay}</span> : null}
                      </span>
                      <time dateTime={toIso(row.timestamp)} className="font-mono tabular-nums text-xs text-slate-400">
                        {formatDateTime(row.timestamp)}
                      </time>
                    </div>

                    <p className="mt-0.5 text-xs text-slate-500">
                      {row.userName ?? row.userEmail ?? 'system'}
                      {row.statusCode ? (
                        <span className={row.succeeded ? 'text-emerald-700' : 'text-red-700'}>
                          {' · '}
                          {row.statusCode} {row.succeeded ? 'OK' : 'Failed'}
                        </span>
                      ) : null}
                    </p>
                  </button>
                </li>
              ))}
            </ol>
          )}
        </div>

        {/* Hidden when everything fits on one page. */}
        {pageCount > 1 ? (
          <div className="shrink-0">
            <IndexPagination
              module={`${module}-history`}
              page={page}
              pageCount={pageCount}
              firstRow={firstRow}
              lastRow={lastRow}
              total={total}
              loading={loading}
              onPageChange={setPage}
            />
          </div>
        ) : null}
      </div>

      <AuditLogDetailModal id={detailId} onClose={() => setDetailId(null)} />
    </>
  );
}
